import React from 'react';
import { useQuery } from 'react-query';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { auth } from '../../../sp-firebase/firebase';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

interface SpotDay {
  date: string;
  price: number | null;
}

async function fetchSpotHistory(formulaCode: string): Promise<SpotDay[]> {
  const user = auth.currentUser;
  if (!user) return [];
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/api/v2/formulas/${formulaCode}/history`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const data = await res.json();
  // Take last 30 entries with valid price
  const all: SpotDay[] = (data.history || [])
    .filter((d: any) => d.price != null)
    .map((d: any) => ({ date: d.date, price: Number(d.price) }));
  return all.slice(-30);
}

interface Props {
  formulaCode: string;
  height?: number;
  /** When true, render only the change badges inline (no chart) */
  inline?: boolean;
}

export const SpotSparkline: React.FC<Props> = ({ formulaCode, height = 48, inline = false }) => {
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { data: spotHistory } = useQuery(
    ['spot-sparkline', formulaCode],
    () => fetchSpotHistory(formulaCode),
    { staleTime: 5 * 60 * 1000, enabled: !!formulaCode },
  );

  const externalTooltipHandler = React.useCallback(({ tooltip, chart }: any) => {
    const el = tooltipRef.current;
    if (!el) return;
    if (tooltip.opacity === 0) { el.style.opacity = '0'; return; }
    const idx = tooltip.dataPoints?.[0]?.dataIndex;
    const date = spotHistory?.[idx]?.date?.slice(0, 10) || '';
    const price = tooltip.dataPoints?.[0]?.parsed?.y;
    el.innerHTML = `<div class="text-[10px] font-medium">${date}</div><div class="text-[10px]">$${price?.toLocaleString(undefined, { maximumFractionDigits: 0 }) ?? ''}</div>`;
    const containerRect = containerRef.current?.getBoundingClientRect();
    const chartRect = chart.canvas.getBoundingClientRect();
    const left = chartRect.left - (containerRect?.left || 0) + tooltip.caretX;
    el.style.opacity = '1';
    el.style.left = `${left}px`;
    el.style.top = '-28px';
  }, [spotHistory]);

  if (!spotHistory || spotHistory.length < 2) return null;

  const prices = spotHistory.map(d => d.price!);
  const first = prices[0];
  const last = prices[prices.length - 1];
  const prev = prices.length >= 2 ? prices[prices.length - 2] : first;

  const changePct30 = ((last - first) / first) * 100;
  const changePct1d = ((last - prev) / prev) * 100;
  const isUp = changePct30 >= 0;
  const is1dUp = changePct1d >= 0;
  const color = isUp ? '#10b981' : '#ef4444';

  const chartData = {
    labels: spotHistory.map(d => d.date.slice(0, 10)),
    datasets: [
      {
        data: prices,
        borderColor: color,
        backgroundColor: isUp ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
      },
    },
    scales: {
      x: { display: false },
      y: { display: false },
    },
    interaction: { intersect: false, mode: 'index' as const },
  };

  const changeBadges = (
    <>
      <span className={`text-[10px] font-medium ${is1dUp ? 'text-green-600' : 'text-red-600'}`}>
        {is1dUp ? '▲' : '▼'}{Math.abs(changePct1d).toFixed(1)}% 1d
      </span>
      <span className={`text-[10px] font-medium ${isUp ? 'text-green-600' : 'text-red-600'}`}>
        {isUp ? '▲' : '▼'}{Math.abs(changePct30).toFixed(1)}% 30d
      </span>
    </>
  );

  if (inline) return changeBadges;

  return (
    <div className="mt-1 relative" ref={containerRef}>
      <div
        ref={tooltipRef}
        className="absolute pointer-events-none rounded z-50 whitespace-nowrap"
        style={{ opacity: 0, background: '#1f2937', color: '#fff', padding: '4px 8px', fontSize: '11px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}
      />
      <div style={{ height }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};
